import {Button, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {Form, FormFieldValidationResult, IFormField} from '@mintdata-wsp/common/components/form/Form';
import {
    ErrorPopupSide,
    FormField,
    IFormFieldProps,
    IFormFieldState
} from '@mintdata-wsp/common/components/formField/FormField';
import {MDIcon} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const inputStyles = require('@mintdata-wsp/common/components/input/Input.less');

export enum InputStyle
{
    Box = 'boxStyle',
    Simple = 'simpleStyle',
    NoBorder = 'noBorderStyle'
}

export enum InputSize
{
    Small = 'smallSize',
    Medium = 'mediumSize',
    Default = 'defaultSize',
    Large = 'largeSize',
    ExtraLarge = 'extraLargeSize'
}

export interface IInputState
{
    isEditMode:boolean;
}

export type InputFormatter = (value:string, props:IInputProps, state:IInputState, formFieldState:IFormFieldState<string>) => string;
export type InputEditableModeRenderer = (value:string, props:IInputProps, state:IInputState, formFieldState:IFormFieldState<string>) => JSX.Element;
export type InputChangeHandler = (newValue:string) => void;
export type InputEditModeResetHandler = () => void;
export type InputEditModeHandler = (resetEditMode:InputEditModeResetHandler) => void;

export interface IInputProps extends IFormFieldProps<string>
{
    isInitiallyFocused?:boolean;
    isInitiallySelected?:boolean;
    placeholder?:string;
    icon?:string;
    isPassword?:boolean;
    isClickForEdit?:boolean;
    isConfirmEdit?:boolean;
    inputClassName?:string;
    editBtnClassName?:string,
    style?:InputStyle;
    size?:InputSize;
    formatter?:InputFormatter;
    editableModeRenderer?:InputEditableModeRenderer;
    onChange?:InputChangeHandler;
    onSetEditMode?:InputEditModeHandler;
    onResetEditMode?:InputEditModeResetHandler;
    autoComplete?:string;
    textPrefix?:string;
    textPostfix?:string;

}

export interface IInputComponentState extends IInputState
{
    styles:IStyles;
}

export class Input extends React.Component<IInputProps, IInputComponentState>
{
    private _inputRef:HTMLInputElement;
    private _form:Form = null;

    constructor(props:IInputProps)
    {
        super(props);
    }

    private _getForm():Form
    {
        const {form} = this.props;

        if( form )
            return form;

        if( !this._form )
            this._form = new Form();

        return this._form;
    }

    @bind
    _onInputRefReady(ref:HTMLInputElement):void
    {
        this._inputRef = ref;
    }

    @bind
    private _focusAndOrSelect(isEditMode:boolean = false):void
    {
        if( this._inputRef )
        {
            const {isInitiallyFocused, isInitiallySelected} = this.props;

            if( isInitiallyFocused || isEditMode )
                this._inputRef.focus();

            if( isInitiallySelected )
                this._inputRef.select();
        }
    }

    @bind
    private _onChange(event:React.ChangeEvent<HTMLInputElement>):void
    {
        const {formKey, onChange, isConfirmEdit} = this.props;

        const newValue:string = event.target.value;
        const form:Form = this._getForm();

        const field:IFormField<string> = form.getField(formKey);

        if( field )
        {
            if( !isConfirmEdit )
            {
                field.change(newValue);

                if( onChange )
                {
                    onChange(newValue);
                }
            }
            else
            {
                field.userInput(newValue);
            }
        }
    }

    @bind
    private _onClickWrapper():void
    {
        let {isReadOnly} = this.props;

        if( !isReadOnly )
            this._inputRef.focus();
    }

    @bind
    private _onSwitchToEditMode():void
    {
        const {onSetEditMode} = this.props;

        this.setState({isEditMode: true});

        if( onSetEditMode && typeof onSetEditMode === 'function' )
            onSetEditMode(this._onResetEditMode);

        setTimeout(() => this._focusAndOrSelect(true));
    }

    @bind
    private _onConfirmEditMode():void
    {
        const {formKey, onChange, onResetEditMode} = this.props;

        const form:Form = this._getForm();

        const field:IFormField<string> = form.getField(formKey);

        if( field )
        {
            const newValue:string = field.getValue();

            field.change(newValue).then((validationResult:FormFieldValidationResult) =>
            {

                if( validationResult.isValid )
                {
                    if( onChange )
                        onChange(newValue);

                    this.setState({isEditMode: false});

                    if( onResetEditMode && typeof onResetEditMode === 'function' )
                        onResetEditMode();
                }
            });
        }
    }

    @bind
    private _onResetEditMode():void
    {
        this.setState({isEditMode: false});

        const {formKey, initialValue, onResetEditMode} = this.props;

        const form:Form = this._getForm();

        const field:IFormField<string> = form.getField(formKey);

        if( field )
            field.reset(initialValue);

        if( onResetEditMode && typeof onResetEditMode === 'function' )
            onResetEditMode();
    }

    @bind
    private _onFocus()
    {
        const {formKey} = this.props;
        const form:Form = this._getForm();

        const field:IFormField<string> = form.getField(formKey);

        if( field )
            field.focus();
    }

    @bind
    private _onBlur()
    {
        const {formKey} = this.props;
        const form:Form = this._getForm();

        const field:IFormField<string> = form.getField(formKey);

        if( field )
            field.blur();
    }

    @bind
    private _onKeyPress(event:React.KeyboardEvent<HTMLInputElement>):void
    {
        const {isConfirmEdit} = this.props;
        const {isEditMode} = this.state;
        const form:Form = this._getForm();

        if( form )
        {
            if( isConfirmEdit && isEditMode && event.key.toLowerCase() === 'enter' )
            {
                this._onConfirmEditMode();
                event.preventDefault();
                event.stopPropagation();
            }
            else if( isConfirmEdit && isEditMode && event.key.toLowerCase() === 'escape' )
            {
                this._onResetEditMode();
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    private _initStyles(styles:IStyles):void
    {
        this.setState({
            styles: Styles.unionStyles(styles, inputStyles)
        });
    }

    public componentWillMount():void
    {
        const {theme, isClickForEdit} = this.props;

        this._initStyles(theme);

        this.setState({
            isEditMode: !isClickForEdit
        });
    }

    public componentDidMount():void
    {
        this._focusAndOrSelect();
    }

    public componentWillReceiveProps(newProps:IInputProps):void
    {
        const {theme} = this.props;

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    @bind
    private _renderContent(formFieldState:IFormFieldState<string>):JSX.Element | Array<JSX.Element>
    {
        const {
            placeholder,
            isPassword,
            icon,
            inputClassName,
            formatter,
            editableModeRenderer,
            autoComplete,
            isClickForEdit,
            isConfirmEdit,
            isReadOnly,
            textPrefix,
            textPostfix,
            formKey
        } = this.props;
        const {isEditMode, styles} = this.state;
        const cx = classnames.bind(styles);

        const elements:Array<JSX.Element> = [];

        if( icon )
            elements.push(<MDIcon key={elements.length} className={cx('icon')} name={icon} />);

        if( isEditMode && textPrefix )
            elements.push(<div key={elements.length} className={cx('prefix')}>{textPrefix}</div>);

        const value:string = formatter
            ? formatter(
                formFieldState.value,
                this.props,
                {
                    isEditMode
                },
                formFieldState
            ) : formFieldState.value;

        if( !isEditMode && editableModeRenderer )
            elements.push(<div key={elements.length} className={cx('editableContent', inputClassName)}>
                {editableModeRenderer(value, this.props, this.state, formFieldState)}
            </div>);
        else
            elements.push(<input
                key={elements.length}
                name={formKey}
                id={formKey}
                className={cx('input', inputClassName)}
                ref={this._onInputRefReady}
                type={isPassword ? 'password' : 'text'}
                placeholder={placeholder}
                onFocus={this._onFocus}
                onBlur={this._onBlur}
                onChange={this._onChange}
                value={value}
                onKeyDownCapture={this._onKeyPress}
                autoComplete={autoComplete}
                readOnly={isReadOnly || !isEditMode}
                disabled={isReadOnly || !isEditMode}
            />);

        if( isEditMode && textPostfix )
            elements.push(<div key={elements.length} className={cx('postfix')}>{textPostfix}</div>);

        if( isClickForEdit && !isEditMode && !isReadOnly )
            elements.push(
                <Button
                    key={elements.length}
                    caption={null}
                    className={cx('editMode', this.props.editBtnClassName)}
                    icon={MDIcons.common.Edit}
                    style={ButtonStyle.SimpleSecondary}
                    onClick={this._onSwitchToEditMode}
                />
            );

        if( isClickForEdit && isConfirmEdit && isEditMode && !isReadOnly )
            elements.push(
                <Button
                    key={elements.length}
                    caption={null}
                    className={cx('editMode')}
                    icon={MDIcons.common.Close}
                    style={ButtonStyle.SimpleSecondary}
                    onClick={this._onResetEditMode}
                />,
                <Button
                    key={elements.length + 1}
                    caption={null}
                    className={cx('editMode')}
                    icon={MDIcons.common.Checkmark}
                    style={ButtonStyle.Simple}
                    onClick={this._onConfirmEditMode}
                />
            );

        return elements;
    }

    public render():JSX.Element
    {
        const {
            style = InputStyle.Box,
            size = InputSize.Default,
            isClickForEdit,
            className,
            initialValue,
            isInitiallyFocused,
            isReadOnly,
            isDisabled,
            validationFn,
            errorPopupSide = ErrorPopupSide.Bottom,
            formKey
        } = this.props;
        const {isEditMode, styles} = this.state;
        const cx = classnames.bind(styles);
        const form:Form = this._getForm();

        const TypedFormField:new() => FormField<string> = FormField as any;

        return (
            <TypedFormField
                className={cx('inputContainer', className, style, size, {isEditMode, isClickForEdit})}
                initialValue={initialValue}
                isInitiallyFocused={isInitiallyFocused}
                isReadOnly={isReadOnly}
                isDisabled={isDisabled}
                validationFn={validationFn}
                errorPopupSide={errorPopupSide}
                formKey={formKey}
                form={form}
                theme={styles}
                content={this._renderContent}
            />
        );
    }
}
